import React, { useEffect, useState } from "react";
import { useAuth, useEmpAuth } from "@/lib/auth";
import { AlertTriangle, ExternalLink, FlaskConical } from "lucide-react";

/**
 * Production host — the ONLY environment employees should use.
 * Anything else (preview.emergentagent.com, localhost, custom dev URLs) is
 * a development / test environment and is gated by this component.
 */
const PRODUCTION_HOST = "hr.spitaligjerman.com";
const PRODUCTION_URL = `https://${PRODUCTION_HOST}`;
const ACK_KEY = "preview_env_ack_v1";

const isPreviewHost = () => {
  if (typeof window === "undefined") return false;
  return window.location.hostname !== PRODUCTION_HOST;
};

/**
 * PreviewEnvironmentGuard
 *
 * Operational protection against employees accidentally using the dev/preview
 * environment for real attendance.
 *
 *  1. On any host != PRODUCTION_HOST, mounts:
 *      • a permanent "TEST SYSTEM" badge top-center
 *      • a thick amber/red outline ring around the viewport
 *      • a blocking warning overlay on first visit (per session)
 *
 *  2. The overlay has a big primary CTA "Hap Sistemin Real" → redirects to the
 *     production URL. A secondary "Vazhdo në testim" link dismisses for the
 *     session — but only for users who can plausibly be testing (we keep this
 *     link visible since auth state isn't known pre-login; admins simply
 *     dismiss it themselves, and any non-admin who logs in is force-redirected
 *     by the post-auth effect below).
 *
 *  3. Once an employee (non-admin/superadmin) is authenticated on a preview
 *     host, we force-redirect them to production. Admins are never auto-
 *     redirected so they can keep testing.
 */
export default function PreviewEnvironmentGuard({ children }) {
  const [preview] = useState(isPreviewHost);
  const [acknowledged, setAcknowledged] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.sessionStorage.getItem(ACK_KEY) === "1"; } catch { return false; }
  });

  // Hooks must run unconditionally — gather auth context. Both providers are
  // guaranteed to be above this component in App.js.
  const authUser = useAuth()?.user || null;
  const empUser = useEmpAuth()?.employee || null;

  const userRoles = authUser?.roles || (authUser?.role ? [authUser.role] : []);
  const isAdmin = userRoles.some((r) => r === "admin" || r === "superadmin");
  const isAuthedNonAdmin = (!!empUser) || (!!authUser && !isAdmin);

  // Force-redirect any non-admin authenticated session away from preview.
  useEffect(() => {
    if (preview && isAuthedNonAdmin) {
      window.location.replace(`${PRODUCTION_URL}/`);
    }
  }, [preview, isAuthedNonAdmin]);

  if (!preview) return children;

  const ackAndContinue = () => {
    try { window.sessionStorage.setItem(ACK_KEY, "1"); } catch { /* ignore */ }
    setAcknowledged(true);
  };

  const goToProduction = () => {
    window.location.replace(`${PRODUCTION_URL}/`);
  };

  return (
    <>
      {/* Permanent amber outline ring around the entire viewport. Pointer-events
          none so it never intercepts clicks. */}
      <div
        aria-hidden
        data-testid="preview-env-ring"
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          boxShadow: "inset 0 0 0 4px #F59E0B",
          zIndex: 9998,
        }}
      />

      {/* Permanent floating badge top-center. Clicking it re-opens the overlay. */}
      <button
        type="button"
        data-testid="preview-env-badge"
        onClick={() => setAcknowledged(false)}
        title="Ky ambient është për testim — kliko për detaje"
        style={{
          position: "fixed",
          top: "max(env(safe-area-inset-top, 0px), 8px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 14px",
          borderRadius: 999,
          background: "#F59E0B",
          color: "#1F2937",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "1px",
          textTransform: "uppercase",
          boxShadow: "0 6px 18px rgba(245,158,11,0.45), 0 0 0 1px rgba(0,0,0,0.04)",
          cursor: "pointer",
        }}
      >
        <FlaskConical size={13} strokeWidth={2.2} />
        Sistemi i Testimit · Test System
      </button>

      {children}

      {/* Blocking warning overlay — shown on first visit per session, or when
          the badge is clicked. */}
      {!acknowledged && (
        <div
          data-testid="preview-env-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="preview-env-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            background: "rgba(15,23,42,0.86)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 560,
              background: "#FFFFFF",
              borderRadius: 24,
              border: "2px solid #F59E0B",
              boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
              padding: "36px 32px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #FBBF24, #F59E0B)",
                margin: "0 auto 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#FFFFFF",
                boxShadow: "0 10px 30px rgba(245,158,11,0.35)",
              }}
            >
              <AlertTriangle size={32} strokeWidth={2.2} />
            </div>

            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                color: "#B45309",
                marginBottom: 8,
              }}
            >
              Development Environment
            </p>

            <h1
              id="preview-env-title"
              style={{
                fontSize: 26,
                fontWeight: 700,
                color: "#0F172A",
                letterSpacing: "-0.3px",
                margin: 0,
              }}
            >
              Sistem Testimi
            </h1>

            <p
              style={{
                marginTop: 14,
                fontSize: 15,
                lineHeight: 1.55,
                color: "#475569",
              }}
            >
              Ky ambient përdoret <strong style={{ color: "#0F172A" }}>vetëm për testime dhe zhvillim</strong>.
              <br />
              Mos kryeni <strong style={{ color: "#B91C1C" }}>check-in / check-out real</strong> këtu —
              përdorni gjithmonë sistemin zyrtar.
            </p>

            <button
              type="button"
              onClick={goToProduction}
              data-testid="goto-production-btn"
              style={{
                marginTop: 26,
                width: "100%",
                minHeight: 60,
                borderRadius: 16,
                background: "linear-gradient(135deg, #10B981, #059669)",
                color: "#FFFFFF",
                border: "none",
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "0.2px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                cursor: "pointer",
                boxShadow: "0 14px 28px rgba(16,185,129,0.35)",
                transition: "transform 150ms ease, box-shadow 150ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 18px 36px rgba(16,185,129,0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 14px 28px rgba(16,185,129,0.35)";
              }}
            >
              <ExternalLink size={20} strokeWidth={2.2} />
              Hap Sistemin Real
            </button>

            <p
              style={{
                marginTop: 14,
                fontSize: 12,
                color: "#64748B",
                wordBreak: "break-all",
              }}
            >
              {PRODUCTION_URL}
            </p>

            <button
              type="button"
              onClick={ackAndContinue}
              data-testid="continue-testing-btn"
              style={{
                marginTop: 22,
                background: "transparent",
                border: "none",
                color: "#64748B",
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: "0.3px",
                cursor: "pointer",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              Vazhdo në testim (vetëm administratorët)
            </button>
          </div>
        </div>
      )}
    </>
  );
}
